import { useCallback, useEffect, useState } from "react";
import { isEqual, omit } from "lodash";
import { AXES_REGEXP, CONFIDENCE_RANGE, MainAxesName, Y_AXES_REGEXP, } from "../types";
import { getLayoutAxesRanges } from "../helpers";
import { isNotEmpty } from "utils/validate";
import { getHeadAxisByParameterName, getSubAxesByParameterName } from "../../../ps-components/Plot/utils";
const handlePlotRelayout = (plotLayout, relayoutAxesRanges, setRelayoutAxesRanges, onRelayout, shouldUpdateLayout) => (event) => {
    const newAxesRanges = {};
    let updateAxes = [];
    Object.keys(event).forEach((key) => {
        const value = event[key];
        const [eventAxisName, rangeKey] = key.split(".");
        let subAxes = [];
        const targetAxis = plotLayout[eventAxisName];
        const headAxis = getHeadAxisByParameterName(targetAxis.parameterName, plotLayout);
        if (headAxis && headAxis.axisName === targetAxis.axisName) {
            subAxes = getSubAxesByParameterName(plotLayout[eventAxisName]?.parameterName, plotLayout);
        }
        updateAxes = [targetAxis, ...subAxes];
        updateAxes.map((axis) => {
            const { axisName } = axis;
            if (rangeKey === "autorange") {
                newAxesRanges[axisName] = { autorange: true, fixedrange: false };
            }
            else if (rangeKey === "range[0]") {
                newAxesRanges[axisName] = {
                    ...newAxesRanges[axisName],
                    min: Number(value),
                };
            }
            else if (rangeKey === "range[1]") {
                newAxesRanges[axisName] = {
                    ...newAxesRanges[axisName],
                    max: Number(value),
                };
            }
        });
    });
    if (!Object.keys(newAxesRanges).length) {
        return;
    }
    Object.keys(newAxesRanges).forEach((key) => {
        const { isConfidence, responseId } = { ...plotLayout[key] };
        if (isConfidence) {
            const { min, max } = { ...newAxesRanges[key] };
            if (min === CONFIDENCE_RANGE[0] && max === CONFIDENCE_RANGE[1]) {
                newAxesRanges[key] = { autorange: true, fixedrange: false };
            }
        }
        newAxesRanges[key] = {
            ...newAxesRanges[key],
            ...(responseId && { responseId }),
        };
    });
    const layoutAxesRanges = getLayoutAxesRanges(plotLayout, relayoutAxesRanges);
    const preparedRelayoutAxesRanges = Object.keys(relayoutAxesRanges).reduce((acc, key) => ({
        ...acc,
        [key]: {
            ...relayoutAxesRanges[key],
            fixedrange: plotLayout[key]?.fixedrange,
        },
    }), {});
    const newState = { ...layoutAxesRanges, ...preparedRelayoutAxesRanges, ...newAxesRanges };
    if (!isEqual(newState, relayoutAxesRanges)) {
        onRelayout && onRelayout(newState, updateAxes.length > 1 ? true : shouldUpdateLayout);
        setRelayoutAxesRanges && setRelayoutAxesRanges(newState);
    }
};
const autoscaleAllAxesRanges = (plotLayout, onRelayout, setRelayoutAxesRanges, onLockButtonClick) => {
    const axesToReset = Object.keys(plotLayout)
        .filter((key) => AXES_REGEXP.test(key))
        .reduce((obj, key) => {
        const { fixedrange, responseId, mergeId } = { ...plotLayout[key] };
        if (!!fixedrange && onLockButtonClick) {
            const isYAxis = Y_AXES_REGEXP.test(key);
            onLockButtonClick(false, isYAxis ? MainAxesName.Y_AXIS : MainAxesName.X_AXIS, responseId);
        }
        return {
            ...obj,
            [key]: { autorange: true, mergeId, ...(responseId && { responseId }), fixedrange: false },
        };
    }, {});
    setRelayoutAxesRanges && setRelayoutAxesRanges(axesToReset);
    onRelayout && onRelayout(axesToReset, true);
};
const updateAllAxesGridlines = (plotLayout, showGrid, onRelayout, setRelayoutAxesRanges) => {
    const axesToReset = Object.keys(plotLayout)
        .filter((key) => AXES_REGEXP.test(key))
        .reduce((obj, key) => {
        const { fixedrange, responseId, mergeId, autorange, range } = {
            ...plotLayout[key],
        };
        const min = (range && range[0]) || null;
        const max = (range && range[1]) || null;
        return {
            ...obj,
            [key]: {
                autorange,
                mergeId,
                ...(responseId && { responseId }),
                min,
                max,
                fixedrange,
                showgrid: showGrid,
            },
        };
    }, {});
    setRelayoutAxesRanges && setRelayoutAxesRanges(axesToReset);
    onRelayout && onRelayout(axesToReset, true);
};
const autoscaleButtonClick = (plotLayout, handleRelayout, onLockButtonClick) => (axis, responseId) => {
    if (axis === MainAxesName.X_AXIS) {
        handleRelayout({ "xaxis.autorange": true }, true);
    }
    else {
        if (!isNotEmpty(responseId)) {
            handleRelayout({ "yaxis.autorange": true }, true);
        }
        else {
            const axisKeyToUpdate = Object.keys(plotLayout)
                .filter((key) => Y_AXES_REGEXP.test(key))
                .find((key) => plotLayout[key]?.responseId === responseId);
            if (axisKeyToUpdate) {
                const eventKey = `${axisKeyToUpdate}.autorange`;
                handleRelayout({ [eventKey]: true }, true);
            }
        }
    }
    onLockButtonClick && onLockButtonClick(false, axis, responseId);
};
// onLockButtonClick function should be used if BE endpoint for update ranges config can't change fixedrange prop
const usePlotRelayout = (plotLayout, onRelayout, onLockButtonClick) => {
    const [relayoutAxesRanges, setRelayoutAxesRanges] = useState({});
    const handleRelayout = useCallback((event, shouldUpdateLayout) => handlePlotRelayout(plotLayout, relayoutAxesRanges, setRelayoutAxesRanges, onRelayout, shouldUpdateLayout)(event), [plotLayout, relayoutAxesRanges, onRelayout]);
    const handleAutoscaleAllAxesRanges = useCallback(() => autoscaleAllAxesRanges(plotLayout, onRelayout, setRelayoutAxesRanges, onLockButtonClick), [onLockButtonClick, onRelayout, plotLayout]);
    const handleAutoscaleButtonClick = useCallback((axis, responseId) => autoscaleButtonClick(plotLayout, handleRelayout, onLockButtonClick)(axis, responseId), [handleRelayout, plotLayout, onLockButtonClick]);
    const handleGridlinesButtonClick = useCallback((showGrid) => updateAllAxesGridlines(plotLayout, showGrid, onRelayout, setRelayoutAxesRanges), [onRelayout, plotLayout]);
    useEffect(() => {
        const existLayoutKeys = Object.keys(plotLayout).filter((key) => AXES_REGEXP.test(key));
        const relayoutRangeAxesKeysToRemove = Object.keys(relayoutAxesRanges).filter((key) => !existLayoutKeys.includes(key));
        if (relayoutRangeAxesKeysToRemove.length) {
            setRelayoutAxesRanges((prevState) => omit(prevState, ...relayoutRangeAxesKeysToRemove));
        }
    }, [plotLayout, relayoutAxesRanges]);
    return {
        handleRelayout,
        handleAutoscaleAllAxesRanges,
        handleAutoscaleButtonClick,
        handleGridlinesButtonClick,
    };
};
export default usePlotRelayout;
